import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Handshake, MapPin, Search, ArrowRight, Building2, HeartHandshake } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_FILTERS = { territoire: '', branche: '', categorie: '', type_contribution: '', search: '' };
const actionClass = 'inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300';

const CATEGORY_LABELS = {
  strategique: 'Sponsor stratégique', territorial: 'Sponsor territorial',
  filiere: 'Sponsor de filière', projet: 'Sponsor de projet',
  competences: 'Sponsor en compétences', nature: 'Sponsor en nature',
  media: 'Sponsor média', institutionnel: 'Partenaire institutionnel',
};

export default function PartenairesSponsorsPage() {
  const [sponsors, setSponsors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [filters, setFilters] = useState({ territoires: [], branches: [], categories: [], types_contribution: [] });
  const [active, setActive] = useState(EMPTY_FILTERS);
  const hasFilters = Object.values(active).some(Boolean);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/api/sponsors/meta/filtres`).then((r) => {
      if (mounted && r.data) setFilters(r.data);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(false);
    const params = {};
    Object.entries(active).forEach(([k, v]) => { if (v) params[k] = v; });
    axios.get(`${API}/api/sponsors`, { params }).then((r) => {
      if (!Array.isArray(r.data?.sponsors)) throw new Error('Invalid sponsors response');
      if (current) {
        setSponsors(r.data.sponsors);
        setTotal(r.data.total || 0);
      }
    }).catch(() => {
      if (current) { setError(true); setSponsors([]); setTotal(0); }
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [active, retryCount]);

  const logoSrc = (s) => (s.logo_url ? (s.logo_url.startsWith('http') ? s.logo_url : `${API}${s.logo_url}`) : null);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="partenaires-sponsors-page">
      <Navigation />
      <main className="max-w-7xl mx-auto px-4 pt-28 pb-16">
        <BackButton />
        <div className="mt-6 mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-300 text-sm font-medium mb-4">
            <Handshake className="w-4 h-4" /> Réseau Partenaires &amp; Sponsors
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Partenaires et sponsors<br /><span className="text-emerald-400">d’OBJECTIF SCOP OUTREMER</span>
          </h1>
          <p className="mt-4 max-w-2xl text-slate-400 text-base">
            Consultez les fiches publiques des organisations qui soutiennent la coopérative.
            Recherchez un partenaire par territoire, branche d’activité ou forme de contribution.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/devenir-sponsor" data-testid="cta-devenir-sponsor" className={`${actionClass} bg-emerald-600 hover:bg-emerald-500 text-white`}>Devenir sponsor <ArrowRight aria-hidden="true" className="w-4 h-4" /></Link>
            <Link to="/projets-a-soutenir" className={`${actionClass} border border-slate-700 text-slate-200 hover:bg-slate-800`}>Projets à soutenir</Link>
          </div>
          <p className="mt-5 text-sm text-slate-300">Pour proposer une coopération autour d’une activité ou d’un service, <Link to="/contact" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-4">contactez l’équipe</Link>.</p>
        </div>

        {/* Filtres */}
        <h2 className="text-2xl font-semibold mb-5">L’annuaire</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-8" data-testid="sponsors-filters">
          <div className="relative md:col-span-1">
            <label htmlFor="sponsors-search" className="sr-only">Rechercher dans l’annuaire</label>
            <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input id="sponsors-search" data-testid="sponsors-search" placeholder="Rechercher" value={active.search}
              onChange={(e) => setActive((a) => ({ ...a, search: e.target.value }))}
              className="pl-9 bg-slate-900 border-slate-800 text-slate-100" />
          </div>
          {[
            ['territoire', 'Tous les territoires', filters.territoires],
            ['branche', 'Toutes les branches', filters.branches],
            ['categorie', 'Toutes les catégories', filters.categories],
            ['type_contribution', 'Tous les soutiens', filters.types_contribution],
          ].map(([key, ph, opts]) => (
            <select key={key} aria-label={ph} data-testid={`sponsors-filter-${key}`} value={active[key]}
              onChange={(e) => setActive((a) => ({ ...a, [key]: e.target.value }))}
              className="bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-200">
              <option value="">{ph}</option>
              {(Array.isArray(opts) ? opts : []).map((o) => <option key={o} value={o}>{CATEGORY_LABELS[o] || o}</option>)}
            </select>
          ))}
        </div>

        {!loading && !error && total > 0 && <p className="text-sm text-slate-300 mb-4" data-testid="sponsors-count" aria-live="polite">{total} fiche{total > 1 ? 's' : ''} publiée{total > 1 ? 's' : ''}{hasFilters ? ' pour votre recherche' : ''}</p>}

        {loading ? (
          <p className="text-slate-300" role="status">Chargement de l’annuaire…</p>
        ) : error ? (
          <Card className="bg-slate-900 border-slate-800" data-testid="sponsors-error">
            <CardContent className="py-12 text-center">
              <h3 className="text-xl font-semibold text-white mb-3">L’annuaire est momentanément indisponible</h3>
              <p className="text-slate-300 mb-5">Les fiches n’ont pas pu être chargées. Vous pouvez réessayer ou nous contacter.</p>
              <Button type="button" onClick={() => setRetryCount((count) => count + 1)} className="bg-emerald-600 hover:bg-emerald-500 rounded-full">Réessayer</Button>
              <Link to="/contact" className="inline-flex ml-5 text-emerald-300 hover:text-emerald-200">Nous contacter</Link>
            </CardContent>
          </Card>
        ) : sponsors.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800" data-testid="sponsors-empty">
            <CardContent className="py-16 text-center">
              <HeartHandshake aria-hidden="true" className="w-12 h-12 mx-auto text-emerald-300 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">{hasFilters ? 'Aucune fiche ne correspond à votre recherche' : 'Les fiches de l’annuaire ne sont pas encore publiées'}</h3>
              <p className="text-slate-300">{hasFilters ? 'Modifiez vos critères pour élargir la recherche.' : 'Vous souhaitez présenter votre organisation ou proposer un soutien ? Décrivez votre contribution dans le parcours sponsor.'}</p>
              {hasFilters ? <Button type="button" onClick={() => setActive(EMPTY_FILTERS)} className="mt-5 bg-emerald-600 hover:bg-emerald-500 rounded-full">Effacer les filtres</Button> : <Link to="/devenir-sponsor" className={`${actionClass} mt-5 bg-emerald-600 hover:bg-emerald-500 text-white`}>Proposer un soutien</Link>}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="sponsors-grid">
            {sponsors.map((s) => (
              <Link key={s.id} to={`/partenaires-sponsors/${s.slug}`} data-testid={`sponsor-card-${s.slug}`}>
                <Card className="h-full bg-slate-900 border-slate-800 hover:border-emerald-600/60 hover:shadow-lg hover:shadow-emerald-900/20 transition-all">
                  <CardContent className="p-6">
                    <div className="h-16 flex items-center mb-4">
                      {logoSrc(s) ? (
                        <img src={logoSrc(s)} alt={s.name} className="max-h-16 max-w-[160px] object-contain" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-slate-800 flex items-center justify-center"><Building2 className="w-7 h-7 text-slate-500" /></div>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-white">{s.name}</h3>
                    {s.sponsor_category && <Badge className="mt-2 bg-emerald-500/15 text-emerald-300 border-0">{CATEGORY_LABELS[s.sponsor_category] || s.sponsor_category}</Badge>}
                    {(s.territories || []).length > 0 && (
                      <p className="mt-3 text-sm text-slate-400 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {s.territories.join(', ')}</p>
                    )}
                    {(s.branches || []).length > 0 && (
                      <p className="mt-1 text-xs text-slate-500">Soutient : {s.branches.join(' · ')}</p>
                    )}
                    <span className="mt-4 inline-flex items-center text-sm text-emerald-400 font-medium">Découvrir le partenaire <ArrowRight className="w-3.5 h-3.5 ml-1" /></span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        <p className="mt-12 text-xs text-slate-400 max-w-3xl leading-relaxed">
          La qualité de sponsor ne confère aucun droit de gouvernance, aucune exclusivité générale et aucun droit
          automatique à l'attribution de marchés, commandes ou prestations. L'éventuelle qualité d'associé de la SCIC
          résulte exclusivement d'une procédure d'admission distincte.
        </p>
      </main>
      <Footer />
    </div>
  );
}
